import React, { useState, useCallback } from 'react';
import {
    Stack,
    Group,
    Text,
    Button,
    Paper,
    Image,
    LoadingOverlay,
    Box,
    Card
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDropzone } from 'react-dropzone';
import { notifications } from '@mantine/notifications';
import { env } from '../../../utils/env';
import { GroupMember } from '../../../types/group';
import { ExpenseForm } from './ExpenseForm';
import uploadIcon from '../../../assets/icons/upload.svg';

export interface ReceiptAnalysisResult {
    imageUrl: string;
    merchant: string | null;
    date: string;
    total: number;
    items: Array<{
        name: string;
        amount: number;
    }>;
}

interface ReceiptScannerProps {
    groupId: string;
    members: GroupMember[];
    accessToken: string;
    onClose: () => void;
    onSuccess: () => void;
    setIsSubmitting: (value: boolean) => void;
}

export const ReceiptScanner = ({
    groupId,
    members,
    accessToken,
    onClose,
    onSuccess,
    setIsSubmitting
}: ReceiptScannerProps) => {
    const [analyzing, setAnalyzing] = useState(false);
    const [receiptResults, setReceiptResults] = useState<ReceiptAnalysisResult[]>([]);
    const [selectedReceiptIndex, setSelectedReceiptIndex] = useState<number | null>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    // Handle file drop
    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        // Only process first file
        if (acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];
        setPreviewImage(URL.createObjectURL(file));

        try {
            setAnalyzing(true);
            // Create form data for file upload
            const formData = new FormData();
            formData.append('file', file);

            // Upload and analyze receipt
            const response = await fetch(`${env.API_URL}/api/ocr/analyze-receipt`, {
                method: 'POST',
                body: formData,
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to analyze receipt');
            }

            const result = await response.json();

            // Prepare the results
            const analysis: ReceiptAnalysisResult = {
                imageUrl: URL.createObjectURL(file),
                merchant: result.merchant || 'Unknown Merchant',
                date: result.date || new Date().toISOString(),
                total: result.total_amount || 0,
                items: result.items || []
            };

            setReceiptResults([analysis]);
            setSelectedReceiptIndex(0);
        } catch (error) {
            console.error('Error analyzing receipt:', error);
            notifications.show({
                title: 'Error',
                message: 'Failed to analyze receipt. Please try again or enter details manually.',
                color: 'red',
            });
        } finally {
            setAnalyzing(false);
        }
    }, [accessToken]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.heic'],
            'application/pdf': ['.pdf']
        },
        maxSize: 10 * 1024 * 1024, // 10MB
        maxFiles: 1,
        multiple: false,
        disabled: analyzing
    });

    // Reset the scanner
    const resetScanner = () => {
        setReceiptResults([]);
        setSelectedReceiptIndex(null);
        setPreviewImage(null);

        if (previewImage) {
            URL.revokeObjectURL(previewImage);
        }
        receiptResults.forEach(result => {
            if (result.imageUrl) {
                URL.revokeObjectURL(result.imageUrl);
            }
        });
    };

    // Render the receipt upload UI if no analysis result yet
    if (receiptResults.length === 0) {
        return (
            <Stack>
                <Box
                    {...getRootProps()}
                    style={{
                        border: '2px dashed #e9ecef',
                        borderRadius: '8px',
                        padding: '32px',
                        textAlign: 'center',
                        cursor: analyzing ? 'default' : 'pointer',
                        position: 'relative'
                    }}
                >
                    <LoadingOverlay visible={analyzing} zIndex={1000} />
                    <input {...getInputProps()} />

                    {previewImage ? (
                        <Stack align="center">
                            <Image
                                src={previewImage}
                                width={200}
                                height={200}
                                fit="contain"
                                alt="Receipt preview"
                            />
                            <Text size="sm">Analyzing receipt...</Text>
                        </Stack>
                    ) : (
                        <Stack align="center">
                            <img src={uploadIcon} alt="Upload" width={48} />
                            <Text size="lg" fw={500}>Upload Receipt</Text>
                            <Text size="sm" c="dimmed">
                                Drop a receipt image here or click to upload
                            </Text>
                            <Text size="xs" c="dimmed">
                                Supported formats: JPG, PNG, HEIC, PDF - Max 10MB
                            </Text>
                        </Stack>
                    )}
                </Box>
            </Stack>
        );
    }

    // Return the expense form once receipt is selected
    if (selectedReceiptIndex !== null) {
        const selectedReceipt = receiptResults[selectedReceiptIndex];

        // Create expense data object from receipt
        const expenseData = {
            description: selectedReceipt.merchant || 'Receipt expense',
            amount: selectedReceipt.total,
            date: new Date(selectedReceipt.date),
            receipt_image_url: selectedReceipt.imageUrl
        };

        return (
            <Stack>
                <Group justify="apart">
                    <Text fw={500}>Receipt Analysis Results</Text>
                    <Button variant="subtle" onClick={resetScanner}>
                        Scan Different Receipt
                    </Button>
                </Group>

                <Card withBorder p="sm" radius="md">
                    <Group>
                        <Image
                            src={selectedReceipt.imageUrl}
                            width={100}
                            height={120}
                            fit="cover"
                            alt="Receipt"
                        />
                        <Stack>
                            <Text fw={500}>{selectedReceipt.merchant || 'Unknown Merchant'}</Text>
                            <Text size="sm">Date: {new Date(selectedReceipt.date).toLocaleDateString()}</Text>
                            <Text size="sm">Total: ${selectedReceipt.total.toFixed(2)}</Text>
                            <Text size="sm">{selectedReceipt.items.length} items detected</Text>
                        </Stack>
                    </Group>
                </Card>

                <ExpenseForm
                    groupId={groupId}
                    members={members}
                    accessToken={accessToken}
                    onClose={onClose}
                    onSuccess={onSuccess}
                    setIsSubmitting={setIsSubmitting}
                    initialData={expenseData}
                />
            </Stack>
        );
    }

    return null;
}; 